import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChevronDown, Save, Calendar, X, AlertCircle, Check,
  ChevronLeft, ChevronRight, Loader2, Building2, Users,
  Upload, FileSpreadsheet, CheckCircle, XCircle, Trash2, Image,
  ScanBarcode, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { attendanceAPI, establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import EstablishmentCard from './EstablishmentCard';
import './AttendanceRegistry.css';
import './ShiftTracker.css';

// Leave type definitions with colors
const LEAVE_TYPES = {
  P: { label: 'Present', code: 'P', color: '#059669', bgColor: '#d1fae5' },
  SL: { label: 'Sick Leave', code: 'SL', color: '#2563eb', bgColor: '#dbeafe' },
  CL: { label: 'Casual Leave', code: 'CL', color: '#7c3aed', bgColor: '#ede9fe' },
  EL: { label: 'Earned Leave', code: 'EL', color: '#0369a1', bgColor: '#e0f2fe' },
  LOP: { label: 'Loss of Pay', code: 'LOP', color: '#c2410c', bgColor: '#fed7aa' },
  CO: { label: 'Comp Off', code: 'CO', color: '#0d9488', bgColor: '#ccfbf1' },
  H: { label: 'Holiday', code: 'H', color: '#dc2626', bgColor: '#fecaca' },
  WO: { label: 'Weekly Off', code: 'WO', color: '#6b7280', bgColor: '#e5e7eb' },
  OT: { label: 'Overtime', code: 'OT', color: '#ea580c', bgColor: '#ffedd5' },
  NR: { label: 'Needs Review', code: 'NR', color: '#92400e', bgColor: '#fef9c3' }
};

// Map database status to frontend code (OT is handled separately in overtime_records)
const mapDbStatusToCode = (status) => {
  const map = {
    'present': 'P',
    'sick': 'SL',
    'casual': 'CL',
    'earned': 'EL',
    'lop': 'LOP',
    'lwp': 'LOP',
    'coff': 'CO',
    'holiday': 'H',
    'weekly_off': 'WO',
    'planned_off': 'WO',
    'needs_review': 'NR'
  };
  return map[status] || 'P';
};

// Day names
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AttendanceRegistry = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState({});
  const [attendanceData, setAttendanceData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [overtimeData, setOvertimeData] = useState({}); // {employeeId: {day: hours}}
  const [originalOvertimeData, setOriginalOvertimeData] = useState({});
  const [holidays, setHolidays] = useState({});
  const [weeklyOffs, setWeeklyOffs] = useState({}); // {employeeId: {weekNumber: offDayNumber}}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownOtHours, setDropdownOtHours] = useState(''); // OT hours input in popup
  const [dropdownOtStartTime, setDropdownOtStartTime] = useState('18:00'); // OT start time
  const [popupSelectedStatus, setPopupSelectedStatus] = useState(''); // Selected status in popup (not saved until Save & Close)
  const [popupOriginalStatus, setPopupOriginalStatus] = useState(''); // Original status when popup opened (to detect if user changed it)
  const [showSummary, setShowSummary] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [resyncingWOs, setResyncingWOs] = useState(false);

  // Attendance type: 'manual' or 'scanner'
  const [attendanceType, setAttendanceType] = useState('manual');
  const [scannerData, setScannerData] = useState(null);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [conflicts, setConflicts] = useState({});

  // Upload state
  const [activeTab, setActiveTab] = useState('grid');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [editedRecords, setEditedRecords] = useState(null); // editable copy of scan records
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadPeriod, setUploadPeriod] = useState('monthly'); // 'weekly' or 'monthly'
  const [selectedWeek, setSelectedWeek] = useState(1); // 1-5
  const [imageOcrEngine, setImageOcrEngine] = useState('claude'); // 'claude' (vision) | 'paddle' (local) | 'vision' (AWS)
  const fileInputRef = useRef(null);

  const gridRef = useRef(null);

  // Calculate days in selected month
  const daysInMonth = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }, [selectedMonth]);

  // Generate array of day info
  const dayInfo = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(year, month, i + 1);
      return {
        day: i + 1,
        dayName: DAY_NAMES[date.getDay()],
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      };
    });
  }, [selectedMonth, daysInMonth]);

  // Show message helper
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch establishments on mount
  useEffect(() => {
    if (lockedEstId) return; // ClientAdmin: use locked est, skip org list
    const fetchEstablishments = async () => {
      try {
        const response = await establishmentAPI.getAll();
        const estabs = response.establishments || [];
        setEstablishments(estabs);
        if (estabs.length > 0 && !selectedEstablishment) {
          setSelectedEstablishment(estabs[0].id.toString());
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        showMessage('Failed to load organizations', 'error');
      }
    };
    fetchEstablishments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load grid data when establishment or month changes (manual mode)
  useEffect(() => {
    if (selectedEstablishment && attendanceType === 'manual') {
      loadGridData();
    }
  }, [selectedEstablishment, selectedMonth, attendanceType]);

  // Load scanner data when in scanner mode
  useEffect(() => {
    if (selectedEstablishment && attendanceType === 'scanner') {
      loadScannerData();
    }
  }, [selectedEstablishment, selectedMonth, attendanceType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeDropdown && !e.target.closest('.attendance-popup')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Close dropdown on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setActiveDropdown(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const loadGridData = async () => {
    setLoading(true);
    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;

      const response = await attendanceAPI.getGridData(
        selectedEstablishment,
        month,
        year
      );

      setEmployees(response.employees || []);
      setLeaveBalances(response.leaveBalances || {});

      // Build attendance grid
      const gridData = {};
      const originalGridData = {};

      (response.employees || []).forEach(emp => {
        gridData[emp.employee_id] = {};
        originalGridData[emp.employee_id] = {};

        // Initialize all days as Present
        for (let day = 1; day <= response.daysInMonth; day++) {
          gridData[emp.employee_id][day] = 'P';
          originalGridData[emp.employee_id][day] = 'P';
        }
      });

      // Apply existing attendance records
      (response.attendance || []).forEach(record => {
        const status = mapDbStatusToCode(record.status);
        if (gridData[record.employee_id]) {
          gridData[record.employee_id][record.day_num] = status;
          originalGridData[record.employee_id][record.day_num] = status;
        }
      });

      // Process holidays
      const holidayMap = {};
      (response.holidays || []).forEach(h => {
        holidayMap[h.day] = h.name;
      });
      setHolidays(holidayMap);

      // Process weekly offs from shift schedules
      // weeklyOffs is { employeeId: { weekNumber: offDayNumber } }
      setWeeklyOffs(response.weeklyOffs || {});

      // Process overtime data
      // overtime is { employeeId: { day: hours } }
      setOvertimeData(response.overtime || {});
      setOriginalOvertimeData(response.overtime || {});

      setAttendanceData(gridData);
      setOriginalData(originalGridData);
      setConflicts(response.conflicts || {});

    } catch (error) {
      console.error('Error loading grid data:', error);
      showMessage('Failed to load attendance data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadScannerData = async () => {
    setScannerLoading(true);
    setScannerData(null);
    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const response = await attendanceAPI.getScannerAttendance(selectedEstablishment, month, year);
      setScannerData(response);
      if (response?.attendance?.length) {
        showMessage(`Scanner: ${response.total_employees || 0} employees, ${response.attendance.length} day records loaded — review and confirm`, 'success');
      }
    } catch (error) {
      console.error('Error loading scanner data:', error);
      showMessage('Failed to load scanner attendance data', 'error');
    } finally {
      setScannerLoading(false);
    }
  };

  const handleSyncScanner = async () => {
    if (!scannerData?.attendance?.length) return;
    setSyncing(true);
    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const result = await attendanceAPI.syncScannerAttendance(
        selectedEstablishment, month, year, scannerData.attendance
      );
      showMessage(`${result.message} — switching to Attendance Grid`, 'success');
      // Switch to manual grid so user sees the final result and can override
      setAttendanceType('manual');
      setActiveTab('grid');
      await loadGridData();
    } catch (error) {
      console.error('Sync error:', error);
      showMessage('Failed to sync scanner data: ' + (error.message || ''), 'error');
    } finally {
      setSyncing(false);
    }
  };

  // Check if a day is a weekly off for an employee.
  // off_day is stored as a week-relative index (0 = first day of the 7-day block,
  // where block 1 = days 1-7, block 2 = days 8-14, etc.) — NOT a JS weekday number.
  const isWeeklyOff = useCallback((employeeId, day) => {
    const empWeeklyOffs = weeklyOffs[employeeId];
    if (!empWeeklyOffs || Object.keys(empWeeklyOffs).length === 0) return false;

    // Calculate week number (1-5) based on day of month
    const weekNumber = Math.ceil(day / 7);

    // Get the off-day index for this specific week
    const offDayForWeek = empWeeklyOffs[String(weekNumber)];
    if (offDayForWeek === undefined) return false;

    // Compute the actual day-of-month that is the weekly off
    const weekStartDay = (weekNumber - 1) * 7 + 1;
    const offActualDay = weekStartDay + parseInt(offDayForWeek);

    return day === offActualDay;
  }, [weeklyOffs]);

  // Handle cell click
  const handleCellClick = (e, employeeId, day) => {
    // Get the actual display status (considering weekly off)
    const storedStatus = attendanceData[employeeId]?.[day] || 'P';
    const isWO = isWeeklyOff(employeeId, day);

    // Determine display status: if it's a WO day and status is P, show as WO
    let displayStatus = storedStatus;
    if (isWO && (storedStatus === 'P' || storedStatus === 'WO')) {
      displayStatus = 'WO';
    }

    const existingOt = overtimeData[employeeId]?.[day];
    const existingOtHours = typeof existingOt === 'object' ? existingOt.hours : (existingOt || '');
    const existingOtStartTime = typeof existingOt === 'object' ? existingOt.start_time : '18:00';

    setPopupSelectedStatus(displayStatus);
    setPopupOriginalStatus(displayStatus); // Track original to detect if user changed it
    setDropdownOtHours(existingOtHours.toString());
    setDropdownOtStartTime(existingOtStartTime || '18:00');
    setActiveDropdown({ employeeId, day });
  };

  // Handle leave type selection - only updates popup local state
  const handleLeaveSelect = (leaveType) => {
    // Just update the local popup selection, don't update attendanceData yet
    setPopupSelectedStatus(leaveType);
  };

  // Handle Save & Close - saves status and OT hours, then closes popup
  const handleSaveAndClose = (employeeId, day) => {
    // Only save the status if user explicitly changed it (different from original)
    const statusChanged = popupSelectedStatus && popupSelectedStatus !== popupOriginalStatus;
    if (statusChanged) {
      setAttendanceData(prev => ({
        ...prev,
        [employeeId]: {
          ...prev[employeeId],
          [day]: popupSelectedStatus
        }
      }));
    }

    // Save OT data if entered (or clear if set to 0)
    const otHours = parseFloat(dropdownOtHours) || 0;
    const existingOt = overtimeData[employeeId]?.[day];
    const existingOtHours = typeof existingOt === 'object' ? existingOt.hours : (existingOt || 0);

    // Only update OT if hours changed
    if (otHours !== existingOtHours) {
      setOvertimeData(prev => ({
        ...prev,
        [employeeId]: {
          ...prev[employeeId],
          [day]: { hours: otHours, start_time: dropdownOtStartTime || '18:00' }
        }
      }));
    }

    setDropdownOtHours('');
    setDropdownOtStartTime('18:00');
    setPopupSelectedStatus('');
    setPopupOriginalStatus('');
    setActiveDropdown(null);
  };

  // Helper to extract OT hours from data (handles both old number format and new object format)
  const getOtHours = (otData) => {
    if (!otData) return 0;
    if (typeof otData === 'object') return otData.hours || 0;
    return otData;
  };

  const getOtStartTime = (otData) => {
    if (!otData) return '18:00';
    if (typeof otData === 'object') return otData.start_time || '18:00';
    return '18:00';
  };

  // Get display status (considering weekly off)
  const getDisplayStatus = useCallback((employeeId, day, storedStatus) => {
    const isWO = isWeeklyOff(employeeId, parseInt(day));
    if (isWO && (storedStatus === 'P' || storedStatus === 'WO')) {
      return 'WO';
    }
    return storedStatus;
  }, [isWeeklyOff]);

  // Calculate attendance changes for save
  const calculateChanges = useCallback(() => {
    const changes = [];

    Object.keys(attendanceData).forEach(employeeId => {
      Object.keys(attendanceData[employeeId]).forEach(day => {
        const current = attendanceData[employeeId][day];
        const original = originalData[employeeId]?.[day] || 'P';
        const currentOtHours = getOtHours(overtimeData[employeeId]?.[day]);
        const originalOtHours = getOtHours(originalOvertimeData[employeeId]?.[day]);
        const currentOtStartTime = getOtStartTime(overtimeData[employeeId]?.[day]);

        // Get display status for summary (shows WO for weekly off days)
        const displayStatus = getDisplayStatus(employeeId, day, current);
        const displayPrevStatus = getDisplayStatus(employeeId, day, original);

        if (current !== original || currentOtHours !== originalOtHours) {
          const employee = employees.find(e => e.employee_id === employeeId);
          changes.push({
            employee_id: employeeId,
            employeeName: `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim(),
            day: parseInt(day),
            status: current,
            previous_status: original,
            display_status: displayStatus,
            display_previous_status: displayPrevStatus,
            ot_hours: currentOtHours,
            ot_start_time: currentOtStartTime,
            previous_ot_hours: originalOtHours
          });
        }
      });
    });

    // Also check for OT-only changes (where status didn't change)
    Object.keys(overtimeData).forEach(employeeId => {
      Object.keys(overtimeData[employeeId] || {}).forEach(day => {
        const currentOtHours = getOtHours(overtimeData[employeeId]?.[day]);
        const originalOtHours = getOtHours(originalOvertimeData[employeeId]?.[day]);
        const currentOtStartTime = getOtStartTime(overtimeData[employeeId]?.[day]);
        const current = attendanceData[employeeId]?.[day] || 'P';
        const original = originalData[employeeId]?.[day] || 'P';

        // Get display status for summary (shows WO for weekly off days)
        const displayStatus = getDisplayStatus(employeeId, day, current);
        const displayPrevStatus = getDisplayStatus(employeeId, day, original);

        // Only add if not already added (status unchanged but OT changed)
        if (current === original && currentOtHours !== originalOtHours) {
          const alreadyAdded = changes.some(c => c.employee_id === employeeId && c.day === parseInt(day));
          if (!alreadyAdded) {
            const employee = employees.find(e => e.employee_id === employeeId);
            changes.push({
              employee_id: employeeId,
              employeeName: `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim(),
              day: parseInt(day),
              status: current,
              previous_status: original,
              display_status: displayStatus,
              display_previous_status: displayPrevStatus,
              ot_hours: currentOtHours,
              ot_start_time: currentOtStartTime,
              previous_ot_hours: originalOtHours
            });
          }
        }
      });
    });

    return changes;
  }, [attendanceData, originalData, overtimeData, originalOvertimeData, employees, getDisplayStatus]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return calculateChanges().length > 0;
  }, [calculateChanges]);

  // Handle save button click
  const handleSave = () => {
    const changes = calculateChanges();

    if (changes.length === 0) {
      showMessage('No changes to save', 'warning');
      return;
    }

    setPendingChanges(changes);
    setShowSummary(true);
  };

  // Confirm and save
  const confirmSave = async () => {
    setSaving(true);
    setShowSummary(false);

    try {
      await attendanceAPI.saveGridChanges({
        establishment_id: parseInt(selectedEstablishment),
        month: selectedMonth.getMonth() + 1,
        year: selectedMonth.getFullYear(),
        changes: pendingChanges.map(c => ({
          employee_id: c.employee_id,
          day: c.day,
          status: c.status,
          previous_status: c.previous_status,
          ot_hours: c.ot_hours || 0,
          ot_start_time: c.ot_start_time || '18:00'
        }))
      });

      notifySavedAndReload(`Successfully saved ${pendingChanges.length} changes`);

      // Reload data to get updated balances
      await loadGridData();

    } catch (error) {
      console.error('Error saving:', error);
      showMessage('Failed to save changes: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setSaving(false);
      setPendingChanges([]);
    }
  };

  // Re-sync weekly offs from shift schedule into work_days_tracking
  const handleResyncWOs = async () => {
    if (!selectedEstablishment) return;
    setResyncingWOs(true);
    try {
      const result = await attendanceAPI.resyncWeeklyOffs({
        establishment_id: parseInt(selectedEstablishment),
        month: selectedMonth.getMonth() + 1,
        year: selectedMonth.getFullYear(),
      });
      showMessage(result.message || `Resynced ${result.changes} records`, 'success');
      await loadGridData();
    } catch (error) {
      showMessage('Resync failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setResyncingWOs(false);
    }
  };

  // Month navigation
  const prevMonth = () => {
    setSelectedMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setSelectedMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const formatMonth = (date) => {
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  // Compute weeks for the selected month (calendar-style: starts day 1, 7-day blocks)
  const monthWeeks = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const dim = new Date(year, month + 1, 0).getDate();
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const mon = monthNames[month];
    const weeks = [];
    for (let w = 0; w < 5; w++) {
      const start = w * 7 + 1;
      const end = Math.min(start + 6, dim);
      if (start > dim) break;
      const startDay = DAY_NAMES[new Date(year, month, start).getDay()];
      const endDay = DAY_NAMES[new Date(year, month, end).getDay()];
      weeks.push({
        num: w + 1, start, end,
        label: `Week ${w + 1} (${start}–${end} ${mon})`,
        dateLabel: `${startDay} ${start} – ${endDay} ${end} ${mon}`,
      });
    }
    return weeks;
  }, [selectedMonth]);

  // Get the selected week's day range
  const selectedWeekRange = useMemo(() => {
    const week = monthWeeks.find(w => w.num === selectedWeek);
    return week || monthWeeks[0] || { start: 1, end: 7, label: 'Week 1', dateLabel: '' };
  }, [selectedWeek, monthWeeks]);

  // Get cell class based on status
  const getCellClass = (status) => {
    const classMap = {
      'P': 'cell-present',
      'SL': 'cell-sl',
      'CL': 'cell-cl',
      'EL': 'cell-el',
      'LOP': 'cell-lop',
      'CO': 'cell-coff',
      'WO': 'cell-wo'
    };
    return classMap[status] || 'cell-present';
  };

  // Classify a day for an employee against their employment window.
  // Returns one of: 'pre' (before they joined), 'exit' (their last working day),
  // 'post' (after their exit), or null (within active window).
  const getEmploymentWindowState = (employee, day) => {
    if (!employee) return null;
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const cell = new Date(year, month, day);
    cell.setHours(0, 0, 0, 0);

    // Parse YYYY-MM-DD as local date — new Date(str) treats it as UTC
    // midnight which can shift the employment window by one day.
    const parseLocalDate = (s) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
      return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
    };
    if (employee.start_date) {
      const start = parseLocalDate(employee.start_date);
      if (start && cell < start) return 'pre';
    }
    if (employee.end_date) {
      const end = parseLocalDate(employee.end_date);
      if (end) {
        if (cell.getTime() === end.getTime()) return 'exit';
        if (cell > end) return 'post';
      }
    }
    return null;
  };

  // ── Upload Handlers ──

  const handleDownloadTemplate = async () => {
    if (!selectedEstablishment) return;
    try {
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();
      const blob = await attendanceAPI.downloadTemplate(selectedEstablishment, month, year);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${String(month).padStart(2, '0')}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showMessage('Failed to download template: ' + (err.message || ''), 'error');
    }
  };

  const getUploadFileType = (file) => {
    if (!file) return null;
    const ext = file.name.split('.').pop().toLowerCase();
    if (['xlsx', 'xls'].includes(ext)) return 'spreadsheet';
    if (['jpg', 'jpeg', 'png', 'webp', 'bmp', 'pdf'].includes(ext)) return 'image';
    return null;
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && getUploadFileType(file)) setUploadFile(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && getUploadFileType(file)) setUploadFile(file);
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedEstablishment) return;
    setUploading(true);
    setParsedResult(null);
    try {
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();
      const fileType = getUploadFileType(uploadFile);
      let result;
      if (fileType === 'image') {
        if (imageOcrEngine === 'claude') {
          result = await attendanceAPI.uploadAttendanceImageVision(uploadFile, selectedEstablishment, month, year, null);
        } else if (imageOcrEngine === 'paddle') {
          result = await attendanceAPI.uploadAttendanceImagePaddle(uploadFile, selectedEstablishment, month, year, null);
        } else {
          result = await attendanceAPI.uploadAttendanceImage(uploadFile, selectedEstablishment, month, year, null);
        }
      } else {
        result = await attendanceAPI.uploadSpreadsheet(uploadFile, selectedEstablishment, month, year, null);
      }
      setParsedResult(result);
      setEditedRecords(result.records ? result.records.map(r => ({ ...r })) : []);
      showMessage(`Scanned ${result.records?.length || 0} records for full month — review and submit`, 'success');
    } catch (err) {
      console.error('Upload error:', err);
      showMessage('Failed to process file: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    const recordsToSave = editedRecords || parsedResult?.records;
    if (!recordsToSave?.length) return;
    setConfirmLoading(true);
    try {
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();
      const saveableRecords = recordsToSave.filter(r => r.status !== 'needs_review');
      const skippedCount = recordsToSave.length - saveableRecords.length;
      const result = await attendanceAPI.confirmSpreadsheet(saveableRecords, selectedEstablishment, month, year);
      const savedCount = result?.count || 0;
      const msg = skippedCount > 0
        ? `Saved ${savedCount} records. ${skippedCount} yellow cells skipped — fix manually in the grid.`
        : `Saved ${savedCount} attendance records from upload`;
      showMessage(msg, skippedCount > 0 ? 'warning' : 'success');
      setParsedResult(null);
      setEditedRecords(null);
      setUploadFile(null);
      setActiveTab('grid');
      await loadGridData();
    } catch (err) {
      console.error('Confirm error:', err);
      showMessage('Failed to save records: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setConfirmLoading(false);
    }
  };

  const removeRecord = (idx) => {
    setParsedResult(prev => ({
      ...prev,
      records: prev.records.filter((_, i) => i !== idx)
    }));
  };

  // Render the dropdown for leave selection
  const renderLeaveDropdown = () => {
    if (!activeDropdown) return null;

    const { employeeId, day } = activeDropdown;
    const currentValue = attendanceData[employeeId]?.[day] || 'P';
    const balance = leaveBalances[employeeId] || {};

    const isWODay = isWeeklyOff(employeeId, day);

    // Check if employee has any leave balance available
    const hasLeaveBalance = (balance.sl_balance > 0) || (balance.cl_balance > 0) || (balance.el_balance > 0);

    // All attendance status options (WO always available to mark a day as weekly off)
    // LOP is disabled if employee has any leave balance - they should use their leaves first
    const leaveOptions = [
      { code: 'P', label: 'Present', balance: null, quota: null },
      { code: 'SL', label: 'Sick Leave', balance: balance.sl_balance, quota: balance.sl_annual_quota },
      { code: 'CL', label: 'Casual Leave', balance: balance.cl_balance, quota: balance.cl_annual_quota },
      { code: 'EL', label: 'Earned Leave', balance: balance.el_balance, quota: balance.el_annual_quota },
      { code: 'LOP', label: 'Loss of Pay', balance: null, quota: null, disabledIfHasLeave: true },
      { code: 'CO', label: 'Comp Off', balance: balance.coff_balance, quota: null },
      { code: 'WO', label: 'Weekly Off', balance: null, quota: null }
    ];

    // OT is only available for P, CO, WO (not for leave types)
    const showOtSection = ['P', 'CO', 'WO'].includes(popupSelectedStatus);

    // Find employee name for display
    const employee = employees.find(e => e.employee_id === employeeId);
    const employeeName = employee ? `${employee.first_name} ${employee.last_name}`.trim() : employeeId;

    return (
      <div className="attendance-popup-overlay" onClick={(e) => {
        // Only close if clicking directly on overlay, not on popup content
        if (e.target === e.currentTarget) {
          setActiveDropdown(null);
        }
      }}>
        <div className="attendance-popup" onClick={(e) => e.stopPropagation()}>
          <div className="attendance-popup-header">
            <div>
              <h3 className="attendance-popup-title">Update Attendance</h3>
              <p className="attendance-popup-subtitle">{employeeName} - Day {day}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); setActiveDropdown(null); }} className="attendance-popup-close">
              <X size={20} />
            </button>
          </div>

          <div className="attendance-popup-content">
            {/* Status Selection */}
            <div className="attendance-popup-section">
              <label className="attendance-popup-label">Attendance Status</label>
              <div className="leave-options-grid">
                {leaveOptions.map(opt => {
                  // Disable if: no balance for leave types, OR LOP when employee has leave balance
                  const isDisabled = (opt.balance !== null && opt.balance <= 0 && opt.code !== currentValue) ||
                                    (opt.disabledIfHasLeave && hasLeaveBalance && opt.code !== currentValue);
                  const isSelected = popupSelectedStatus === opt.code;
                  const leaveInfo = LEAVE_TYPES[opt.code];

                  // Show hint for LOP when disabled due to available leave
                  const lopDisabledHint = opt.disabledIfHasLeave && hasLeaveBalance && opt.code !== currentValue;

                  return (
                    <button
                      key={opt.code}
                      className={`leave-option-btn ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      style={{
                        borderColor: isSelected ? leaveInfo?.color : '#e5e7eb',
                        backgroundColor: isSelected ? leaveInfo?.bgColor : '#ffffff',
                        color: isSelected ? leaveInfo?.color : '#374151'
                      }}
                      disabled={isDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLeaveSelect(opt.code);
                      }}
                      title={lopDisabledHint ? 'Use available leave balance (SL/CL/EL) first' : ''}
                    >
                      <span className="leave-option-code">{opt.code}</span>
                      <span className="leave-option-label">{opt.label}</span>
                      {opt.balance !== null && opt.quota !== null && (
                        <span
                          className={`leave-option-balance ${opt.balance <= 0 ? 'zero' : ''}`}
                          title={`Available: ${opt.balance} | Annual Quota: ${opt.quota}`}
                        >
                          <span style={{ fontSize: '0.70rem', opacity: 0.7, display: 'block', marginBottom: '1px' }}>
                            Available/Quota
                          </span>
                          {opt.balance}/{opt.quota}
                        </span>
                      )}
                      {opt.balance !== null && opt.quota === null && (
                        <span className={`leave-option-balance ${opt.balance <= 0 ? 'zero' : ''}`}>
                          Bal: {opt.balance}
                        </span>
                      )}
                      {lopDisabledHint && (
                        <span className="leave-option-balance zero" style={{ fontSize: '0.65rem' }}>
                          Use SL/CL/EL
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* OT Hours Input - only show for P, CO, WO */}
            {showOtSection && (
            <div className="attendance-popup-section ot-section">
              <label className="attendance-popup-label">
                ⏱️ Add Overtime (keeps current status + adds OT hours)
              </label>
              <div className="ot-inputs-row" onClick={(e) => e.stopPropagation()}>
                <div className="ot-field">
                  <label className="ot-input-label">OT Hours</label>
                  <input
                    type="number"
                    min="0"
                    max="12"
                    step="0.5"
                    value={dropdownOtHours}
                    onChange={(e) => setDropdownOtHours(e.target.value)}
                    placeholder="0"
                    className="ot-input ot-hours-field"
                  />
                </div>
                <div className="ot-field">
                  <label className="ot-input-label">Start Time</label>
                  <div className="ot-time-picker">
                    <select
                      value={(() => {
                        const hr24 = parseInt(dropdownOtStartTime.split(':')[0] || '18');
                        if (hr24 === 0) return '12';
                        if (hr24 > 12) return String(hr24 - 12);
                        return String(hr24);
                      })()}
                      onChange={(e) => {
                        const mins = dropdownOtStartTime.split(':')[1] || '00';
                        const hr24 = parseInt(dropdownOtStartTime.split(':')[0] || '18');
                        const isPM = hr24 >= 12;
                        let newHr24 = parseInt(e.target.value);
                        if (isPM) {
                          newHr24 = newHr24 === 12 ? 12 : newHr24 + 12;
                        } else {
                          newHr24 = newHr24 === 12 ? 0 : newHr24;
                        }
                        setDropdownOtStartTime(`${String(newHr24).padStart(2, '0')}:${mins}`);
                      }}
                      className="ot-time-select"
                    >
                      {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
                        <option key={h} value={String(h)}>{h}</option>
                      ))}
                    </select>
                    <span className="ot-time-separator">:</span>
                    <select
                      value={dropdownOtStartTime.split(':')[1] || '00'}
                      onChange={(e) => {
                        const hrs = dropdownOtStartTime.split(':')[0] || '18';
                        setDropdownOtStartTime(`${hrs}:${e.target.value}`);
                      }}
                      className="ot-time-select"
                    >
                      <option value="00">00</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="45">45</option>
                    </select>
                    <select
                      value={parseInt(dropdownOtStartTime.split(':')[0] || '18') >= 12 ? 'PM' : 'AM'}
                      onChange={(e) => {
                        const mins = dropdownOtStartTime.split(':')[1] || '00';
                        const hr24 = parseInt(dropdownOtStartTime.split(':')[0] || '18');
                        const currentHr12 = hr24 === 0 ? 12 : (hr24 > 12 ? hr24 - 12 : hr24);
                        let newHr24;
                        if (e.target.value === 'PM') {
                          newHr24 = currentHr12 === 12 ? 12 : currentHr12 + 12;
                        } else {
                          newHr24 = currentHr12 === 12 ? 0 : currentHr12;
                        }
                        setDropdownOtStartTime(`${String(newHr24).padStart(2, '0')}:${mins}`);
                      }}
                      className="ot-time-select ot-ampm-select"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          <div className="attendance-popup-footer">
            <button
              className="attendance-popup-btn cancel"
              onClick={(e) => {
                e.stopPropagation();
                setActiveDropdown(null);
              }}
            >
              Cancel
            </button>
            <button
              className="attendance-popup-btn save"
              onClick={(e) => {
                e.stopPropagation();
                handleSaveAndClose(employeeId, day);
              }}
            >
              <Check size={16} />
              Save & Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render summary modal
  const renderSummaryModal = () => {
    if (!showSummary) return null;

    return (
      <div className="summary-modal-overlay" onClick={() => setShowSummary(false)}>
        <div className="summary-modal" onClick={e => e.stopPropagation()}>
          <div className="summary-modal-header">
            <h3 className="summary-modal-title">Confirm Changes</h3>
            <button onClick={() => setShowSummary(false)} className="summary-modal-close">
              <X size={20} />
            </button>
          </div>
          <div className="summary-modal-content">
            <p className="summary-modal-subtitle">
              You are about to save {pendingChanges.length} attendance change(s):
            </p>
            <div className="summary-changes-list">
              {pendingChanges.map((change, idx) => (
                <div key={idx} className="summary-change-item">
                  <span className="summary-employee">{change.employeeName}</span>
                  <span className="summary-day">Day {change.day}</span>
                  <span className="summary-change">
                    {change.display_status !== change.display_previous_status ? (
                      <>
                        <span className={`summary-status ${getCellClass(change.display_previous_status, change.day)}`}>
                          {change.display_previous_status}
                        </span>
                        <span className="summary-arrow">→</span>
                        <span className={`summary-status ${getCellClass(change.display_status, change.day)}`}>
                          {change.display_status}
                        </span>
                      </>
                    ) : (
                      <span className={`summary-status ${getCellClass(change.display_status, change.day)}`}>
                        {change.display_status}
                      </span>
                    )}
                    {(change.ot_hours > 0 || change.previous_ot_hours > 0) && (
                      <span className="summary-ot">
                        {change.ot_hours !== change.previous_ot_hours
                          ? `OT: ${change.previous_ot_hours || 0}h → ${change.ot_hours || 0}h`
                          : `OT: ${change.ot_hours}h`
                        }
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="summary-modal-actions">
            <button
              onClick={() => setShowSummary(false)}
              className="summary-btn summary-btn-cancel"
            >
              Cancel
            </button>
            <button
              onClick={confirmSave}
              className="summary-btn summary-btn-confirm"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Confirm & Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="attendance-grid-container">
      {/* Header */}
      <div className="attendance-grid-header-section">
        <div className="attendance-grid-title-row">
          <Calendar className="attendance-grid-icon" size={28} />
          <div>
            <h1 className="attendance-grid-title">Monthly Attendance Grid</h1>
            <p className="attendance-grid-subtitle">
              View and manage attendance for all employees
            </p>
          </div>
        </div>
      </div>

      {/* Establishment Selector — hidden for ClientAdmin */}
      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstablishment}
          onChange={(e) => setSelectedEstablishment(e.target.value)}
        />
      )}

      {/* Filters */}
      <div className="attendance-grid-filters">
        <div className="attendance-filter-group">
          <label className="attendance-filter-label">
            <span className="filter-icon-wrapper month">
              <Calendar size={15} />
            </span>
            Month
          </label>
          <div className="month-picker">
            <button onClick={prevMonth} className="month-nav-btn">
              <ChevronLeft size={18} />
            </button>
            <span className="month-label">{formatMonth(selectedMonth)}</span>
            <button onClick={nextMonth} className="month-nav-btn">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="attendance-filter-group">
          <label className="attendance-filter-label">
            <span className="filter-icon-wrapper" style={{ background: attendanceType === 'scanner' ? '#dbeafe' : '#ede9fe' }}>
              {attendanceType === 'scanner' ? <ScanBarcode size={15} /> : <Calendar size={15} />}
            </span>
            Attendance Type
          </label>
          <select
            value={attendanceType}
            onChange={(e) => { setAttendanceType(e.target.value); setActiveTab('grid'); }}
            className="month-picker"
            style={{ padding: '0.4rem 0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500, background: 'var(--theme-surface, #ffffff)' }}
          >
            <option value="manual">Manual</option>
            {/* Scanner option disabled — OCR accuracy issues */}
            {/* <option value="scanner">Scanner</option> */}
          </select>
        </div>

        {employees.length > 0 && attendanceType === 'manual' && (
          <div className="attendance-filter-group">
            <label className="attendance-filter-label">
              <span className="filter-icon-wrapper employees">
                <Users size={15} />
              </span>
              Employees
            </label>
            <div className="employee-count-badge">
              <span className="employee-count-number">{employees.length}</span>
              {employees.length === 1 ? 'Employee' : 'Employees'}
            </div>
          </div>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`attendance-message attendance-message-${message.type}`}>
          <AlertCircle size={16} />
          {message.text}
        </div>
      )}

      {/* ── SCANNER MODE ── */}
      {attendanceType === 'scanner' && selectedEstablishment && (
        <>
          {scannerLoading && (
            <div className="attendance-loading">
              <Loader2 className="attendance-loading-spinner" size={40} />
              <p>Loading scanner attendance data...</p>
            </div>
          )}

          {!scannerLoading && scannerData && (
            <div className="st-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
                <h2 className="st-card-title" style={{ margin: 0 }}>
                  <ScanBarcode size={18} /> Scanner Attendance — {formatMonth(selectedMonth)}
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={loadScannerData} className="st-btn-secondary" style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}>
                    <RefreshCw size={14} /> Refresh
                  </button>
                  <button
                    onClick={handleSyncScanner}
                    disabled={syncing || !scannerData?.attendance?.length}
                    className="st-btn-primary"
                    style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
                  >
                    {syncing ? <><Loader2 size={14} className="spin" /> Syncing...</> : <><CheckCircle size={14} /> Confirm & Sync to Attendance</>}
                  </button>
                </div>
              </div>

              {/* Scanner Summary Cards */}
              {scannerData.scanner_summary && Object.keys(scannerData.scanner_summary).length > 0 && (
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {(() => {
                    const totals = { present: 0, absent: 0, holiday: 0, weekly_off: 0, total_scans: 0 };
                    Object.values(scannerData.scanner_summary).forEach(s => {
                      totals.present += s.present; totals.absent += s.absent;
                      totals.holiday += s.holiday; totals.weekly_off += s.weekly_off;
                      totals.total_scans += s.total_scans;
                    });
                    return [
                      { label: 'Total Scans', value: totals.total_scans, bg: '#dbeafe', color: '#1d4ed8' },
                      { label: 'Present Days', value: totals.present, bg: '#d1fae5', color: '#059669' },
                      { label: 'Absent Days', value: totals.absent, bg: '#fed7aa', color: '#c2410c' },
                      { label: 'Employees', value: (scannerData.employees || []).length, bg: '#ede9fe', color: '#7c3aed' },
                    ].map(c => (
                      <div key={c.label} style={{ padding: '0.6rem 1rem', borderRadius: '10px', background: c.bg, minWidth: '100px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: c.color }}>{c.value}</div>
                        <div style={{ fontSize: '0.75rem', color: c.color, opacity: 0.8 }}>{c.label}</div>
                      </div>
                    ));
                  })()}
                </div>
              )}

              {/* Scanner Grid */}
              {(scannerData.employees || []).length > 0 && (
                <div className="attendance-grid-wrapper">
                  <table className="attendance-grid-table">
                    <thead>
                      <tr>
                        <th className="employee-header">Employee</th>
                        {Array.from({ length: scannerData.daysInMonth || daysInMonth }, (_, i) => {
                          const day = i + 1;
                          const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
                          const dayName = DAY_NAMES[date.getDay()];
                          const isHoliday = (scannerData.holidays || []).find(h => h.day === day);
                          return (
                            <th key={day} className={`date-header ${isHoliday ? 'holiday' : ''} ${date.getDay() === 0 || date.getDay() === 6 ? 'weekend' : ''}`}>
                              <div className="date-header-day">{day}</div>
                              <div className="date-header-name">{dayName}</div>
                              {isHoliday && <div className="date-header-holiday" title={isHoliday.name}>H</div>}
                            </th>
                          );
                        })}
                        <th className="date-header" style={{ minWidth: '50px' }}>P</th>
                        <th className="date-header" style={{ minWidth: '50px' }}>A</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(scannerData.employees || []).map(emp => {
                        const summary = scannerData.scanner_summary?.[emp.employee_id] || {};
                        const empAttendance = {};
                        (scannerData.attendance || []).forEach(a => {
                          if (a.employee_id === emp.employee_id) empAttendance[a.day_num] = a;
                        });
                        return (
                          <tr key={emp.employee_id}>
                            <td className="employee-cell">
                              <div className="employee-info">
                                <span className="employee-name">{emp.first_name} {emp.last_name}</span>
                                <span className="employee-id">{emp.employee_id}</span>
                              </div>
                            </td>
                            {Array.from({ length: scannerData.daysInMonth || daysInMonth }, (_, i) => {
                              const day = i + 1;
                              const rec = empAttendance[day];
                              const status = rec?.status || '';
                              const code = mapDbStatusToCode(status);
                              const info = LEAVE_TYPES[code];
                              const title = rec?.first_in && rec?.last_out
                                ? `In: ${rec.first_in} | Out: ${rec.last_out} | ${rec.hours_worked || '-'}h`
                                : rec?.first_in ? `In: ${rec.first_in}` : (info?.label || '');
                              return (
                                <td key={day} className="attendance-cell-td">
                                  <div
                                    className={`attendance-cell disabled`}
                                    style={{ backgroundColor: info?.bgColor || '#f1f5f9', color: info?.color || '#94a3b8', cursor: 'default' }}
                                    title={title}
                                  >
                                    <span className="cell-status">{status ? code : '-'}</span>
                                    {rec?.scan_count > 0 && (
                                      <span style={{ fontSize: '0.55rem', position: 'absolute', bottom: '1px', right: '2px', color: '#6366f1', fontWeight: 600 }}>
                                        {rec.scan_count}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                            <td className="attendance-cell-td">
                              <div style={{ textAlign: 'center', fontWeight: 600, color: '#059669', fontSize: '0.8rem' }}>{summary.present || 0}</div>
                            </td>
                            <td className="attendance-cell-td">
                              <div style={{ textAlign: 'center', fontWeight: 600, color: '#c2410c', fontSize: '0.8rem' }}>{summary.absent || 0}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {(scannerData.employees || []).length === 0 && (
                <div className="attendance-empty-state">
                  <ScanBarcode size={48} className="attendance-empty-icon" />
                  <h3>No Scanner Data</h3>
                  <p>No barcode scan records found for this period</p>
                </div>
              )}
            </div>
          )}

          {!scannerLoading && !scannerData && (
            <div className="attendance-empty-state">
              <ScanBarcode size={64} className="attendance-empty-icon" />
              <h3>Select an Organization</h3>
              <p>Choose an organization to view scanner attendance</p>
            </div>
          )}
        </>
      )}

      {/* ── MANUAL MODE ── */}

      {/* Legend */}
      {attendanceType === 'manual' && selectedEstablishment && employees.length > 0 && (
        <div className="attendance-legend">
          {Object.entries(LEAVE_TYPES).map(([code, info]) => (
            <div key={code} className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: info.bgColor, color: info.color }}
              >
                {code}
              </span>
              <span className="legend-label">{info.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      {attendanceType === 'manual' && selectedEstablishment && (
        <div className="st-tabs" style={{ marginBottom: '1rem' }}>
          <button
            className={`st-tab ${activeTab === 'grid' ? 'st-tab-active' : ''}`}
            onClick={() => setActiveTab('grid')}
          >
            <Calendar size={16} /> Attendance Grid
          </button>
          <button
            className={`st-tab ${activeTab === 'upload' ? 'st-tab-active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            <Upload size={16} /> Upload Attendance
          </button>
        </div>
      )}

      {/* Upload Tab */}
      {attendanceType === 'manual' && activeTab === 'upload' && selectedEstablishment && (
        <div className="st-card">
          <div className="st-card-header">
            <h2 className="st-card-title">
              <Upload size={20} /> Upload Attendance
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
              Upload attendance for {new Date(selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' })} — Full Month
            </p>
          </div>

          {/* Download Template */}
          {!parsedResult && (
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.25rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <button className="st-btn st-btn-primary" onClick={handleDownloadTemplate} style={{ gap: '0.5rem' }}>
                <FileSpreadsheet size={16} /> Download Monthly Template (.xlsx)
              </button>
            </div>
          )}

          {/* Upload */}
          {!parsedResult && (
            <>
              <div
                className={`st-dropzone ${dragOver ? 'st-dropzone-active' : ''} ${uploadFile ? 'st-dropzone-has-file' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.jpg,.jpeg,.png,.webp,.bmp,.pdf" onChange={handleFileSelect} style={{ display: 'none' }} />
                {uploadFile ? (
                  <div className="st-dropzone-file">
                    {getUploadFileType(uploadFile) === 'image'
                      ? <Image size={32} style={{ color: '#6366f1' }} />
                      : <FileSpreadsheet size={32} style={{ color: '#059669' }} />}
                    <div>
                      <p className="st-dropzone-filename">{uploadFile.name}</p>
                      <p className="st-dropzone-filesize">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button className="st-dropzone-remove" onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}>
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="st-dropzone-empty">
                    <Upload size={40} style={{ color: '#94a3b8', marginBottom: '0.5rem' }} />
                    <p style={{ fontWeight: 600, color: 'var(--theme-text)' }}>Drop file here or click to browse</p>
                    <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                      Spreadsheet or photo of full month attendance (Days 1–{daysInMonth})
                    </p>
                  </div>
                )}
              </div>

              {uploadFile && getUploadFileType(uploadFile) === 'image' && (
                <div className="st-ocr-engine" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.75rem 0', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--theme-text)' }}>Scan engine:</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="radio" name="ocrEngine" checked={imageOcrEngine === 'claude'}
                      onChange={() => setImageOcrEngine('claude')} disabled={uploading} />
                    Claude vision (any sheet, handwriting)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="radio" name="ocrEngine" checked={imageOcrEngine === 'paddle'}
                      onChange={() => setImageOcrEngine('paddle')} disabled={uploading} />
                    PaddleOCR (local, fixed grid)
                  </label>
                </div>
              )}

              {uploadFile && (
                <div className="st-upload-actions">
                  <button className="st-btn st-btn-primary" onClick={handleUpload} disabled={uploading}>
                    {uploading
                      ? <><Loader2 size={16} className="spin" /> {getUploadFileType(uploadFile) === 'image' ? 'Scanning...' : 'Parsing...'}</>
                      : <><Upload size={16} /> {getUploadFileType(uploadFile) === 'image' ? 'Scan & Extract' : 'Parse & Preview'}</>}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Preview parsed results */}
          {parsedResult && (
            <div className="st-preview">
              <div className="st-preview-summary">
                <div className="st-preview-stat">
                  <CheckCircle size={16} style={{ color: '#059669' }} />
                  <span>{parsedResult.records?.length || 0} records parsed</span>
                </div>
                <div className="st-preview-stat">
                  <Users size={16} style={{ color: '#6366f1' }} />
                  <span>{parsedResult.total_employees || 0} employees matched</span>
                </div>
                {parsedResult.unmatched?.length > 0 && (
                  <div className="st-preview-stat">
                    <XCircle size={16} style={{ color: '#dc2626' }} />
                    <span>{parsedResult.unmatched.length} unmatched IDs</span>
                  </div>
                )}
              </div>

              {parsedResult.summary && (
                <div style={{ padding: '0.5rem 0.75rem', background: '#eff6ff', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.8rem', color: '#1e40af' }}>
                  {parsedResult.summary}
                </div>
              )}

              {/* Claude vision: per-employee counts computed in Python + reconciliation */}
              {parsedResult.analysis?.employees && (() => {
                const a = parsedResult.analysis;
                const ss = a.sheet_summary || {};
                const cats = ['present', 'absent', 'off', 'leave', 'holiday', 'other', 'unclear'];
                const ok = ss.validation_status === 'valid';
                return (
                  <details open={!ok} style={{ marginBottom: '1rem' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: ok ? '#059669' : '#b45309', marginBottom: '0.4rem' }}>
                      Scan validation: {ss.validation_status} — {ss.total_employees} employees × {ss.total_attendance_days_per_employee} days
                      {' '}(blank cells counted as {ss.blank_cells_counted_as})
                    </summary>
                    <div style={{ overflowX: 'auto', maxHeight: '320px', overflowY: 'auto', fontSize: '0.78rem' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr style={{ textAlign: 'left', color: '#475569' }}>
                            <th style={{ padding: '3px 8px' }}>Employee</th>
                            <th style={{ padding: '3px 8px' }}>Days</th>
                            {cats.map(c => <th key={c} style={{ padding: '3px 8px', textTransform: 'capitalize' }}>{c}</th>)}
                            <th style={{ padding: '3px 8px' }}>Issues</th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.employees.map((e, i) => (
                            <tr key={i} style={{ borderTop: '1px solid #e2e8f0', background: e.issues?.length ? '#fffbeb' : undefined }}>
                              <td style={{ padding: '3px 8px', fontWeight: 600 }}>{e.employee_name}{!e.employee_id && ' (unmatched)'}</td>
                              <td style={{ padding: '3px 8px' }}>{e.total_days}</td>
                              {cats.map(c => <td key={c} style={{ padding: '3px 8px' }}>{e[c]}</td>)}
                              <td style={{ padding: '3px 8px', color: '#b45309' }}>{e.issues?.join('; ')}</td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: '2px solid #94a3b8', fontWeight: 600 }}>
                            <td style={{ padding: '3px 8px' }}>Total</td>
                            <td />
                            {cats.map(c => <td key={c} style={{ padding: '3px 8px' }}>{a.grand_totals?.[c]}</td>)}
                            <td />
                          </tr>
                        </tbody>
                      </table>
                      {a.issues?.length > 0 && (
                        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem', color: '#b45309' }}>
                          {a.issues.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      )}
                    </div>
                  </details>
                );
              })()}

              {/* Raw exceptions debug view */}
              {parsedResult.raw_exceptions && (
                <details style={{ marginBottom: '1rem' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#6366f1', marginBottom: '0.4rem' }}>
                    Raw Scanned OFF Days ({parsedResult.raw_exceptions.length} employees)
                  </summary>
                  <div style={{ overflowX: 'auto', maxHeight: '300px', overflowY: 'auto', fontSize: '0.78rem' }}>
                    {parsedResult.raw_exceptions.map((exc, i) => (
                      <div key={i} style={{ padding: '3px 8px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '1rem' }}>
                        <span style={{ fontWeight: 600, minWidth: '80px' }}>{exc.emp_id}</span>
                        <span style={{ minWidth: '160px', color: '#475569' }}>{exc.name}</span>
                        <span style={{ color: '#6366f1' }}>
                          OFF days: {exc.off_days?.length > 0 ? exc.off_days.join(', ') : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {parsedResult.unmatched?.length > 0 && (
                <div style={{ padding: '0.75rem', background: '#fef2f2', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem', color: '#991b1b' }}>
                  <strong>Unmatched employee IDs:</strong> {parsedResult.unmatched.join(', ')}
                </div>
              )}

              {parsedResult.debug && (
                <details style={{ marginBottom: '1rem' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#6366f1' }}>
                    Debug: Parser Info
                  </summary>
                  <pre style={{ fontSize: '0.72rem', background: '#f1f5f9', padding: '0.75rem', borderRadius: '8px', overflow: 'auto', maxHeight: '200px' }}>
                    {JSON.stringify(parsedResult.debug, null, 2)}
                  </pre>
                </details>
              )}

              {/* Editable attendance grid — click any cell to cycle status */}
              {(editedRecords || parsedResult.records)?.length > 0 && (() => {
                const records = editedRecords || parsedResult.records;
                const empMap = {};
                records.forEach(rec => {
                  const eid = rec.employee_id;
                  if (!empMap[eid]) empMap[eid] = { name: rec.employee_name || eid, id: eid, days: {} };
                  const wd = rec.work_date || '';
                  const dayNum = wd ? parseInt(wd.split('-')[2]) : null;
                  if (dayNum) empMap[eid].days[dayNum] = rec.status;
                });
                const empList = Object.values(empMap);
                const yr = selectedMonth.getFullYear();
                const mo = selectedMonth.getMonth();
                const dim = new Date(yr, mo + 1, 0).getDate();

                const statusCycle = ['present', 'weekly_off'];
                const statusStyle = {
                  present:    { bg: '#dcfce7', color: '#166534', label: 'P' },
                  weekly_off: { bg: '#e5e7eb', color: '#374151', label: 'OFF' },
                };

                const toggleCell = (empId, day) => {
                  setEditedRecords(prev => prev.map(r => {
                    const wd = r.work_date || '';
                    const d = wd ? parseInt(wd.split('-')[2]) : null;
                    if (r.employee_id === empId && d === day) {
                      const curIdx = statusCycle.indexOf(r.status);
                      const nextStatus = statusCycle[(curIdx + 1) % statusCycle.length];
                      return { ...r, status: nextStatus };
                    }
                    return r;
                  }));
                };

                return (
                  <>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.5rem' }}>
                      Click any cell to toggle P ↔ OFF
                    </div>
                    <div className="attendance-grid-wrapper">
                      <table className="attendance-grid-table">
                        <thead>
                          <tr>
                            <th className="employee-header">Employee</th>
                            {Array.from({ length: dim }, (_, i) => {
                              const d = new Date(yr, mo, i + 1);
                              return (
                                <th key={i + 1} className={`date-header ${d.getDay() === 0 || d.getDay() === 6 ? 'weekend' : ''}`}>
                                  <div className="date-header-day">{i + 1}</div>
                                  <div className="date-header-name">{DAY_NAMES[d.getDay()]}</div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {empList.map(emp => (
                            <tr key={emp.id}>
                              <td className="employee-cell">
                                <div className="employee-info">
                                  <span className="employee-name">{emp.name}</span>
                                  <span className="employee-id">{emp.id}</span>
                                </div>
                              </td>
                              {Array.from({ length: dim }, (_, i) => {
                                const day = i + 1;
                                const status = emp.days[day] || 'present';
                                const st = statusStyle[status] || statusStyle.present;
                                return (
                                  <td key={day} className="attendance-cell-td" onClick={() => toggleCell(emp.id, day)}
                                    title={`Day ${day} — ${status} (click to change)`}>
                                    <div
                                      className="attendance-cell"
                                      style={{
                                        backgroundColor: st.bg,
                                        color: st.color,
                                        cursor: 'pointer', minWidth: '28px', minHeight: '28px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700,
                                      }}
                                    >
                                      {st.label}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}

              <div className="st-upload-actions" style={{ marginTop: '1rem' }}>
                <button className="st-btn st-btn-secondary" onClick={() => { setParsedResult(null); setEditedRecords(null); setUploadFile(null); }}>
                  Cancel
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem' }}>
                  <button
                    className="st-btn st-btn-primary"
                    onClick={handleConfirmUpload}
                    disabled={confirmLoading || !(editedRecords || parsedResult.records)?.length}
                  >
                    {confirmLoading
                      ? <><Loader2 size={16} className="spin" /> Saving...</>
                      : <><CheckCircle size={16} /> Submit & Save to DB ({(editedRecords || parsedResult.records)?.length} records)</>}
                  </button>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    Overwrites existing attendance with what was scanned
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {attendanceType === 'manual' && loading && activeTab === 'grid' && (
        <div className="attendance-loading">
          <Loader2 className="attendance-loading-spinner" size={40} />
          <p>Loading attendance data...</p>
        </div>
      )}

      {/* Empty State - No Organization */}
      {attendanceType === 'manual' && !loading && activeTab === 'grid' && !selectedEstablishment && (
        <div className="attendance-empty-state">
          <Building2 size={64} className="attendance-empty-icon" />
          <h3>Select an Organization</h3>
          <p>Choose an organization to view the attendance grid</p>
        </div>
      )}

      {/* Empty State - No Employees */}
      {attendanceType === 'manual' && !loading && activeTab === 'grid' && selectedEstablishment && employees.length === 0 && (
        <div className="attendance-empty-state">
          <Users size={64} className="attendance-empty-icon" />
          <h3>No Employees Found</h3>
          <p>This organization has no active employees</p>
        </div>
      )}

      {/* Grid Table */}
      {attendanceType === 'manual' && !loading && activeTab === 'grid' && employees.length > 0 && (
        <>
          <div className="attendance-grid-wrapper" ref={gridRef}>
            <table className="attendance-grid-table">
              <thead>
                <tr>
                  <th className="employee-header">Employee</th>
                  {dayInfo.map(({ day, dayName, isWeekend }) => (
                    <th
                      key={day}
                      className={`date-header ${isWeekend ? 'weekend' : ''}`}
                    >
                      <div className="date-header-day">{day}</div>
                      <div className="date-header-name">{dayName}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map(employee => (
                  <tr key={employee.employee_id}>
                    <td className="employee-cell">
                      <div className="employee-info">
                        <span className="employee-name">
                          {employee.first_name} {employee.last_name}
                        </span>
                        <span className="employee-id">{employee.employee_id}</span>
                      </div>
                    </td>
                    {dayInfo.map(({ day }) => {
                      const windowState = getEmploymentWindowState(employee, day);

                      // Outside the employment window — render a locked, greyed cell
                      if (windowState === 'pre' || windowState === 'post') {
                        const label = windowState === 'post' ? 'Exited' : 'Not yet joined';
                        return (
                          <td key={day} className="attendance-cell-td">
                            <div
                              className="attendance-cell disabled"
                              title={`${label} on ${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`}
                              style={{ background: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed' }}
                            >
                              <span className="cell-status">—</span>
                            </div>
                          </td>
                        );
                      }

                      const status = attendanceData[employee.employee_id]?.[day] || 'P';
                      const isHoliday = holidays[day];
                      const isWO = isWeeklyOff(employee.employee_id, day);

                      // Show saved status as-is (spreadsheet is source of truth)
                      let displayStatus = status;
                      let cellTitle = LEAVE_TYPES[status]?.label;

                      if (!isHoliday && isWO) {
                        // If it's a weekly off day but user marked OT or other status, show that
                        if (status !== 'P' && status !== 'WO') {
                          displayStatus = status;
                          cellTitle = LEAVE_TYPES[status]?.label;
                        } else {
                          displayStatus = 'WO';
                          cellTitle = 'Weekly Off';
                        }
                      }

                      // Termination day itself — show as exit marker, still styled
                      const isExitDay = windowState === 'exit';
                      if (isExitDay) {
                        displayStatus = 'EX';
                        cellTitle = `Last working day (${employee.end_date})`;
                      }

                      const isEditable = !isExitDay;
                      const otHours = getOtHours(overtimeData[employee.employee_id]?.[day]);
                      const conflict = conflicts[employee.employee_id]?.[String(day)];

                      let title = otHours > 0 ? `${cellTitle} + ${otHours}h OT` : cellTitle;
                      if (conflict) {
                        title = `⚠ CONFLICT: ${conflict.source} detected "${conflict.detected}" but DB has "${conflict.current}" — click to resolve`;
                      }

                      return (
                        <td key={day} className="attendance-cell-td">
                          <div
                            className={`attendance-cell ${getCellClass(displayStatus, day)} ${isEditable ? 'editable' : 'disabled'} ${otHours > 0 ? 'has-ot' : ''} ${conflict ? 'has-conflict' : ''}`}
                            onClick={(e) => isEditable && handleCellClick(e, employee.employee_id, day)}
                            title={title}
                            style={isExitDay ? { background: '#fde68a', color: '#92400e', fontWeight: 600 } : undefined}
                          >
                            <span className="cell-status">{displayStatus}</span>
                            {otHours > 0 && (
                              <span className="cell-ot-badge">{otHours}h</span>
                            )}
                            {conflict && (
                              <span className="cell-conflict-flag" title={`${conflict.source}: ${conflict.detected}`}>
                                <AlertTriangle size={10} />
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Save Button */}
          <div className="attendance-save-section">
            {Object.keys(conflicts).length > 0 && (
              <span className="conflicts-indicator">
                <AlertTriangle size={14} />
                {Object.values(conflicts).reduce((sum, days) => sum + Object.keys(days).length, 0)} conflict(s) — hover cells with red flags to review
              </span>
            )}
            {hasChanges && (
              <span className="changes-indicator">
                {calculateChanges().length} unsaved change(s)
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="attendance-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </>
      )}

      {/* Leave Dropdown */}
      {renderLeaveDropdown()}

      {/* Summary Modal */}
      {renderSummaryModal()}
    </div>
  );
};

export default AttendanceRegistry;
